import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { advanceSearchValue, AvDecision, defaultSearchValue } from '../constants/ticketlist.constants';

export const useFormHandler = () => {
    const intl = useIntl();
    const searchSchema = z.object({
        ticketStatus: z.string().optional(),
        ticketAssignment: z.string().optional(),
        watchStatusBasic: z.string().optional(),
        workgroup: z.string().optional(),
    });

    const cancelModalSchema = z.object({
        comment: z.string().optional(),
    });

    const completeTicketSchema = z.object({
        comments: z.string(),
    });

    const verifyCompleteTicketSchema = z
        .object({
            internalComment: z.string().min(1, {
                message: 'Please enter a comment.',
            }),
            externalComment: z.string().optional(),
            markDocumentAs: z.string().min(1, {
                message: 'Please enter a status.',
            }),
            rejectReason: z.string().optional(),
        })
        .refine(
            data => {
                if (data.markDocumentAs === 'rejected') {
                    return !!data.rejectReason;
                }
                return true;
            },
            {
                message: 'Please select a reason.',
                path: ['rejectReason'],
            },
        );

    const avManualReviewSchema = z
        .object({
            decision: z.string().min(1),
            rejectReason: z.string().optional(),
            internalComment: z.string().min(1),
            externalComment: z.string().optional(),
            improvementFeedback: z.string().min(1),
        })
        .refine(data => (data.decision === AvDecision.REJECTED ? !!data.rejectReason : true), {
            path: ['rejectReason'],
        });

    const advanceSearchSchema = z
        .object({
            ticketSubject: z.string().optional(),
            ticketComments: z.string().optional(),
            ticketQueues: z.array(z.string()).optional(),
            ticketPriority: z.array(z.string()).optional(),
            ticketStatus: z.array(z.string()).optional(),
            ticketNumber: z.string().optional(),
            submittedAfter: z.string().or(z.date()).or(z.null()).optional(),
            submittedBefore: z.string().or(z.date()).or(z.null()).optional(),
            assignee: z.string().optional(),
            assigneeId: z.string().optional(),
            tagId: z.string().optional(),
            requestedByName: z.string().optional(),
            requestedBy: z.string().optional(),
            requestedBytagId: z.string().optional(),
            userRole: z.string().optional(),
            requester: z.string().optional(),
            roleId: z.string().optional(),
            customFieldSelect: z.string().or(z.number()).optional(),
            customFieldSelectVal: z.string().or(z.date()).or(z.null()).or(z.number()).optional(),
            customFieldSelectValOptionId: z.string().optional(),
            ticketCategorySelect: z.string().or(z.number()).optional(),
            ticketType: z.array(z.string()).optional(),
            workGroupBytagId: z.string().optional(),
            watchStatusAdvanced: z.array(z.string()).optional(),
            confidentialTicketsFilter: z.array(z.string()).optional(),
        })
        .superRefine((data, ctx) => {
            if (data.submittedAfter && data.submittedBefore) {
                if (data.submittedAfter > data.submittedBefore) {
                    ctx.addIssue({
                        code: 'custom',
                        message: intl.formatMessage({ id: 'ticketList.errors.dateError' }),
                        path: ['submittedBefore'],
                    });
                }
            }
            if (data.userRole && !data.requester) {
                ctx.addIssue({
                    code: 'custom',
                    message: intl.formatMessage({ id: 'common.field_required' }),
                    path: ['requester'],
                });
            }
        });

    const reassignTicketSchema = z.object({
        assignee: z
            .string({
                required_error: intl.formatMessage({ id: 'ticketList.errors.selectAssignee' }),
            })
            .min(1),
    });

    const bulkReassignTicketSchema = z.object({
        assignee: z
            .string({
                required_error: intl.formatMessage({ id: 'ticketList.errors.selectAssignee' }),
            })
            .min(1),
    });

    const deafultSearchForm = useForm({
        defaultValues: defaultSearchValue,
        resolver: zodResolver(searchSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
    });

    const cancelTicketForm = useForm({
        defaultValues: { comment: '' },
        resolver: zodResolver(cancelModalSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
    });

    const completeTicketForm = useForm({
        defaultValues: { comments: '' },
        resolver: zodResolver(completeTicketSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
    });

    const verifyCompleteTicketForm = useForm({
        defaultValues: { internalComment: '', externalComment: '', markDocumentAs: '', rejectReason: '' },
        resolver: zodResolver(verifyCompleteTicketSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    const reassignTicketForm = useForm({
        defaultValues: { assignee: '' },
        resolver: zodResolver(reassignTicketSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    const avManualReviewForm = useForm({
        defaultValues: {
            decision: '',
            rejectReason: '',
            internalComment: '',
            externalComment: '',
            improvementFeedback: '',
        },
        resolver: zodResolver(avManualReviewSchema),
        // onChange so Modal submit disable tracks Zod rules (single source of truth).
        mode: 'onChange',
        reValidateMode: 'onChange',
    });

    const bulkReassignTicketForm = useForm({
        defaultValues: { assignee: '' },
        resolver: zodResolver(bulkReassignTicketSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    const advanceSearchForm = useForm({
        defaultValues: advanceSearchValue,
        resolver: zodResolver(advanceSearchSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
    });
    return {
        cancelTicketForm,
        completeTicketForm,
        verifyCompleteTicketForm,
        reassignTicketForm,
        deafultSearchForm,
        bulkReassignTicketForm,
        advanceSearchForm,
        avManualReviewForm,
    };
};
